import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, Check, Download, FileText, MapPin, Search, ShieldCheck } from 'lucide-react';
import { api, ApiError } from '../lib/api';
import { NoCargo } from './NoCargo';
import { avisarFalloDeCarga } from '../lib/fallos';

/** Sin estado propio: estas dos cajas no tienen un hueco donde pintar el aviso, así que va a la
 *  barra global (la misma que alimenta api.ts). Lo que NO puede pasar es que no diga nada. */
const avisarFallo = () => avisarFalloDeCarga('datos');
import { useFallo } from '../lib/useFallo';
import { scrollBoxToEnd } from '../lib/scrollHelpers';
import { useI18n } from '../lib/i18n';
import { uploadFile } from '../lib/upload';
import { FileDrop } from './FileDrop';

/**
 * Policy Reader (Coverage Detector) — portado del app standalone Coverage-Detector
 * (PolicyDetailPage.jsx + RayChat.jsx). Lee la póliza con IA (extracción con citas
 * verbatim, red flags, attack routes, highlights), permite chatear con Ray sobre la
 * póliza, marcar ítems para el resumen y exportar el reporte PDF. Va en Claim Review.
 */

type DataPoint = { value: string; explanation: string; citation_text: string; page: string; confidence: string; where_to_see: string };
type Extracted = Record<string, any>; // eslint-disable-line @typescript-eslint/no-explicit-any
type Policy = { id: string; claim_type: string; carrier: string | null; policy_number: string | null; status: string; created_at: string; extracted_data?: Extracted; report_selections?: string[]; error_detail?: string | null };
type ListItem = { id: string; claim_type: string; carrier: string | null; policy_number: string | null; status: string; created_at: string };
type ChatMsg = { id: string; role: string; content: string; citations: { quote: string; page: string }[]; added_to_report: boolean };
/** La lectura única de la póliza (mig 0504): lo que la pantalla muestra del Policy JSON además de la forma vieja. */
type LecturaPolizaFront = {
  meta: { status: string; semaforo: 'green' | 'yellow' | 'red' | null; model: string | null; latency_ms: number | null; read_at: string | null; copied: boolean; scanned: boolean | null; rescue_model: string | null; form_count: number | null };
  reading: { forms: Array<{ form_number: string; title: string | null; kind: string; page_from: number | null; page_to: number | null; listed_in_declarations: boolean; found_in_document: boolean; summary: string | null; modifies: string[] }> };
  validation: { semaforo: 'green' | 'yellow' | 'red'; reasons: string[]; forms_missing: string[]; findings_modified_by_endorsement: number };
};

const SECTION_ORDER = [
  'identification', 'parties', 'coverages', 'valuation', 'deductibles', 'perils',
  'roofing', 'water', 'fire', 'tree', 'endorsements_exclusions', 'conditions',
];
const CLAIM_TYPES = ['roof', 'water', 'fire', 'tree'];
const SEVS = ['good', 'caution', 'alert'];
const prettify = (k: string) => k.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
const shortVal = (v: string) => (v && v.length > 28 ? `${v.slice(0, 27)}…` : v || '');
const asListItems = (v: string) => v.split(/\s*;\s*/).map((s) => s.trim().replace(/\.$/, '')).filter(Boolean);

const sevColor: Record<string, { bg: string; fg: string }> = {
  good: { bg: 'var(--green-soft)', fg: 'var(--green)' },
  caution: { bg: 'var(--orange-soft)', fg: 'var(--orange)' },
  alert: { bg: 'var(--red-soft)', fg: 'var(--red)' },
  high: { bg: 'var(--red-soft)', fg: 'var(--red)' },
  medium: { bg: 'var(--orange-soft)', fg: 'var(--orange)' },
  low: { bg: 'var(--surface-2)', fg: 'var(--muted)' },
};

function buildBadges(data: Extracted): { label: string; value: string; sev: string; ref: string }[] {
  if (Array.isArray(data.highlights) && data.highlights.length) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return data.highlights.filter((h: any) => h && h.label && h.ref).map((h: any) => ({
      label: h.label, value: shortVal(h.value), sev: SEVS.includes(h.severity) ? h.severity : 'caution', ref: h.ref,
    }));
  }
  const out: { label: string; value: string; sev: string; ref: string }[] = [];
  const push = (label: string, dp: DataPoint | undefined, ref: string, sev: string) => {
    if (dp && dp.confidence !== 'not_found' && dp.value && dp.value.trim()) out.push({ label, value: shortVal(dp.value.trim()), sev, ref });
  };
  const id = data.identification || {}; const ded = data.deductibles || {};
  push('Policy type', id.policy_form, 'identification.policy_form', 'good');
  push('AOP deductible', ded.aop, 'deductibles.aop', 'caution');
  push('Hurricane', ded.hurricane_named_storm, 'deductibles.hurricane_named_storm', 'caution');
  push('Wind / hail', ded.wind_hail, 'deductibles.wind_hail', 'caution');
  return out;
}

// ── Ray chat (policy-scoped) ──
function RayChat({ policyId, onCitePage }: { policyId: string; onCitePage?: (page: string) => void }) {
  const { t, lang } = useI18n();
  const [messages, setMessages] = useState<ChatMsg[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState('');
  const endRef = useRef<HTMLDivElement>(null);

  const load = useCallback(() => api.get<{ messages: ChatMsg[] }>(`/api/policy-reader/policies/${policyId}/chat`).then((r) => setMessages(r.messages)).catch(avisarFallo), [policyId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { scrollBoxToEnd(endRef.current); }, [messages, sending]);

  async function send() {
    const q = input.trim();
    if (!q || sending) return;
    setErr(''); setSending(true);
    setMessages((m) => [...m, { id: `tmp-${m.length}`, role: 'user', content: q, citations: [], added_to_report: false }]);
    setInput('');
    try { await api.post(`/api/policy-reader/policies/${policyId}/chat`, { message: q, language: lang === 'es' ? 'es' : 'en' }); await load(); }
    catch (e) { setErr(e instanceof ApiError ? e.message : t('pr.rayError')); await load(); } finally { setSending(false); }
  }
  async function toggleReport(msg: ChatMsg) {
    const next = !msg.added_to_report;
    setMessages((m) => m.map((x) => (x.id === msg.id ? { ...x, added_to_report: next } : x)));
    try { await api.patch(`/api/policy-reader/chat/${msg.id}`, { added_to_report: next }); } catch { load(); }
  }

  return (
    <div className="card" style={{ display: 'grid', gap: 8 }}>
      <h2 className="card-title" style={{ margin: 0 }}><Search size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('pr.rayTitle')}</h2>
      <div className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{t('pr.raySubtitle')}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 340, overflowY: 'auto', padding: '4px 0' }}>
        {messages.map((m) => (
          <div key={m.id} style={{ alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start', maxWidth: '92%', background: m.role === 'user' ? 'var(--accent-soft)' : 'var(--surface-2)', borderRadius: 10, padding: '8px 11px' }}>
            <div style={{ fontSize: 'var(--fs-sm)', whiteSpace: 'pre-wrap' }}>{m.content}</div>
            {m.role === 'assistant' && Array.isArray(m.citations) && m.citations.map((c, i) => (
              <blockquote key={i} style={{ margin: '6px 0 0', borderLeft: '3px solid var(--accent-2)', paddingLeft: 8, fontSize: 'var(--fs-2xs)', fontStyle: 'italic', color: 'var(--muted)' }}>
                “{c.quote}”{c.page && (onCitePage
                  ? <button type="button" onClick={() => onCitePage(c.page)} style={{ fontStyle: 'normal', border: 'none', background: 'none', padding: 0, marginLeft: 4, cursor: 'pointer', color: 'var(--accent)', fontWeight: 600 }}><FileText size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('pr.page')} {c.page}</button>
                  : <span style={{ fontStyle: 'normal' }}> — {t('pr.page')} {c.page}</span>)}
              </blockquote>
            ))}
            {m.role === 'assistant' && !String(m.id).startsWith('tmp-') && (
              <button className="btn ghost sm" style={{ marginTop: 6, fontSize: 'var(--fs-2xs)', color: m.added_to_report ? 'var(--green)' : undefined }} onClick={() => toggleReport(m)}>
                {m.added_to_report ? <><Check size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('pr.addedToReport')}</> : `+ ${t('pr.addToReport')}`}
              </button>
            )}
          </div>
        ))}
        {sending && <div className="muted" style={{ fontSize: 'var(--fs-xs)' }}>{t('pr.thinking')}</div>}
        <div ref={endRef} />
      </div>
      {err && <div style={{ color: 'var(--red)', fontSize: 'var(--fs-xs)' }}>{err}</div>}
      <div style={{ display: 'flex', gap: 6 }}>
        <input className="input sm" style={{ flex: 1 }} value={input} placeholder={t('pr.rayPlaceholder')} disabled={sending}
          onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') send(); }} />
        <button className="btn sm" disabled={sending || !input.trim()} onClick={send}>{t('pr.send')}</button>
      </div>
    </div>
  );
}

// ── Un datapoint (valor + explicación + cita + checkmark para el resumen) ──
function DataPointRow({ fieldKey, dp, selKey, selected, onToggle, onCitePage }: { fieldKey: string; dp: DataPoint; selKey: string; selected: boolean; onToggle: (k: string, v: boolean) => void; onCitePage: (page: string) => void }) {
  const { t } = useI18n();
  const found = dp.confidence !== 'not_found' && (dp.value || dp.citation_text);
  const items = dp.value ? asListItems(dp.value) : [];
  return (
    <div id={`pr-item-${selKey}`} style={{ borderBottom: '1px solid var(--line)', padding: '8px 0', opacity: found ? 1 : 0.55, background: selected ? 'var(--accent-soft)' : undefined, borderRadius: selected ? 8 : 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontWeight: 'var(--fw-semibold)', fontSize: 'var(--fs-xs)', flex: 1 }}>{prettify(fieldKey)}</span>
        {dp.confidence && dp.confidence !== 'not_found' && <span className="badge" style={{ fontSize: 'var(--fs-2xs)', ...sevBadge(dp.confidence) }}>{dp.confidence}</span>}
        {found && (
          <button className="btn ghost sm" style={{ padding: '0 7px', color: selected ? 'var(--green)' : undefined }} title={selected ? t('pr.inSummary') : t('pr.selectItem')} onClick={() => onToggle(selKey, !selected)}><Check size={13} strokeWidth={1.75} /></button>
        )}
      </div>
      {dp.value && (items.length > 1
        ? <ul style={{ margin: '4px 0 0', paddingLeft: 18, fontSize: 'var(--fs-sm)' }}>{items.map((it, i) => <li key={i}>{it}</li>)}</ul>
        : <div style={{ fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-medium)', marginTop: 2 }}>{dp.value}</div>)}
      {dp.explanation && <div className="muted" style={{ fontSize: 'var(--fs-2xs)', marginTop: 3 }}>{dp.explanation}</div>}
      {dp.citation_text && (
        <blockquote style={{ margin: '6px 0 0', borderLeft: '3px solid var(--accent-2)', paddingLeft: 8, fontSize: 'var(--fs-2xs)', fontStyle: 'italic', color: 'var(--muted)' }}>
          “{dp.citation_text}”{dp.page && (
            <button type="button" onClick={() => onCitePage(dp.page)} title={t('pr.goToPage')}
              style={{ fontStyle: 'normal', border: 'none', background: 'none', padding: 0, marginLeft: 4, cursor: 'pointer', color: 'var(--accent)', fontWeight: 600 }}>
              <FileText size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('pr.page')} {dp.page}
            </button>
          )}
        </blockquote>
      )}
      {found && dp.where_to_see && dp.where_to_see.trim() && <div style={{ fontSize: 'var(--fs-2xs)', color: 'var(--accent)', marginTop: 3 }}><MapPin size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('pr.whereToSee')}: {dp.where_to_see}</div>}
    </div>
  );
}
const sevBadge = (c: string) => ({ background: sevColor[c]?.bg ?? 'var(--surface-2)', color: sevColor[c]?.fg ?? 'var(--muted)' });

// ── Detalle de una póliza ──
function PolicyDetail({ policyId, onBack }: { policyId: string; onBack: () => void }) {
  const { t } = useI18n();
  const [policy, setPolicy] = useState<Policy | null>(null);
  const [starting, setStarting] = useState(false);
  const [err, setErr] = useState('');
  const [activeTab, setActiveTab] = useState<string | null>(null);
  const [selections, setSelections] = useState<Set<string>>(new Set());
  const [pdfPage, setPdfPage] = useState(1);
  const analyzing = starting || policy?.status === 'processing';
  const goToPage = useCallback((pageStr: string) => {
    const n = parseInt(String(pageStr).match(/\d+/)?.[0] ?? '', 10);
    if (!Number.isNaN(n)) setPdfPage(n);
  }, []);

  const load = useCallback(() => api.get<{ policy: Policy }>(`/api/policy-reader/policies/${policyId}`).then((r) => {
    setPolicy(r.policy); setSelections(new Set(Array.isArray(r.policy.report_selections) ? r.policy.report_selections : []));
  }).catch(() => {}), [policyId]);
  useEffect(() => { load(); }, [load]);

  // La extracción corre en background: mientras esté 'processing', hacemos polling.
  useEffect(() => {
    if (policy?.status !== 'processing') return undefined;
    const id = setInterval(() => { load(); }, 3000);
    return () => clearInterval(id);
  }, [policy?.status, load]);
  // La lectura única (mig 0504): índice de formularios + semáforo. Se pide cuando la póliza quedó leída.
  const [lectura, setLectura] = useState<LecturaPolizaFront | null>(null);
  useEffect(() => {
    if (policy?.status !== 'extracted') { if (policy?.status === 'processing') setLectura(null); return; }
    api.get<LecturaPolizaFront>(`/api/policy-reader/policies/${policyId}/reading`).then(setLectura).catch(() => setLectura(null));
  }, [policy?.status, policyId]);

  const data = policy?.extracted_data && Object.keys(policy.extracted_data).length > 1 ? policy.extracted_data : null;
  const badges = useMemo(() => (data ? buildBadges(data) : []), [data]);
  const tabs = SECTION_ORDER.filter((s) => data && data[s]);
  const current = activeTab && data && data[activeTab] ? activeTab : tabs[0];

  function toggleSelection(selKey: string, selected: boolean) {
    setSelections((prev) => { const n = new Set(prev); if (selected) n.add(selKey); else n.delete(selKey); return n; });
    api.patch(`/api/policy-reader/policies/${policyId}/selection`, { fieldKey: selKey, selected }).catch(() => load());
  }
  // Analyze = re-correr el ANÁLISIS sobre la lectura hecha (~30 s); Re-read = releer el PDF desde cero (2-4 min).
  async function analyze(force = false) {
    if (force && !window.confirm(t('pr.rereadConfirm'))) return;
    setStarting(true); setErr('');
    try { await api.post(`/api/policy-reader/policies/${policyId}/extract`, force ? { force: true } : {}); await load(); }
    catch (e) { setErr(e instanceof ApiError ? e.message : 'Error'); }
    finally { setStarting(false); }
  }
  function jumpTo(ref: string) {
    setActiveTab(ref.split('.')[0] || null);
    setTimeout(() => document.getElementById(`pr-item-${ref}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 60);
  }
  function openReport(path: string) { window.open(`/api/policy-reader/policies/${policyId}/${path}`, '_blank'); }
  async function viewPdf() { try { const r = await api.get<{ url: string }>(`/api/policy-reader/policies/${policyId}/file`); window.open(r.url, '_blank'); } catch { /* */ } }

  if (!policy) return <div className="muted" style={{ fontSize: 'var(--fs-sm)' }}>{t('common.loadingDots')}</div>;
  const redFlags = data?.red_flags ?? [];
  const attackRoutes = data?.attack_routes ?? [];

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <button className="btn ghost sm" onClick={onBack}>← {t('common.back')}</button>
        <span className="badge gray" style={{ fontSize: 'var(--fs-2xs)' }}>{policy.claim_type?.toUpperCase()}</span>
        <span className="badge" style={{ fontSize: 'var(--fs-2xs)', ...sevBadge(policy.status === 'error' ? 'high' : policy.status === 'extracted' ? 'good' : 'medium') }}>{policy.status}</span>
        <span style={{ flex: 1 }} />
        <button className="btn ghost sm" onClick={viewPdf}><FileText size={13} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('pr.viewPdf')}</button>
      </div>

      {/* Identidad + highlights */}
      <div className="card" style={{ display: 'grid', gap: 8 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8, fontSize: 'var(--fs-sm)' }}>
          <div><div className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{t('pr.insurer')}</div><b>{policy.carrier || '—'}</b></div>
          <div><div className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{t('pr.policyNumber')}</div><b>{policy.policy_number || '—'}</b></div>
        </div>
        {data && badges.length > 0 && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {badges.map((b, i) => (
              <button key={`${b.ref}-${i}`} className="badge" style={{ fontSize: 'var(--fs-2xs)', cursor: 'pointer', border: 'none', ...sevBadge(b.sev) }} title={t('pr.jumpTo')} onClick={() => jumpTo(b.ref)}>
                <b>{b.label}</b>{b.value ? ` · ${b.value}` : ''}
              </button>
            ))}
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn sm" onClick={() => void analyze(false)} disabled={analyzing}>{analyzing ? t('pr.analyzing') : data ? t('pr.reanalyze') : t('pr.analyze')}</button>
          {data && <button className="btn ghost sm" onClick={() => void analyze(true)} disabled={analyzing} title={t('pr.rereadConfirm')}>{t('pr.reread')}</button>}
          {data && <button className="btn ghost sm" onClick={() => openReport('report')}><Download size={13} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('pr.exportPdf')}</button>}
          {data && selections.size > 0 && <button className="btn ghost sm" onClick={() => openReport('selected-report.pdf')}><Check size={13} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('pr.exportSummary')} ({selections.size})</button>}
        </div>
        {analyzing && <div className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{t('pr.analyzingHint')}</div>}
        {policy.status === 'error' && <div style={{ color: 'var(--red)', fontSize: 'var(--fs-xs)' }}><AlertTriangle size={13} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {policy.error_detail || t('pr.extractFailed')}</div>}
        {err && <div style={{ color: 'var(--red)', fontSize: 'var(--fs-xs)' }}>{err}</div>}
      </div>

      {!data && !analyzing && policy.status !== 'error' && <div className="muted" style={{ fontSize: 'var(--fs-sm)' }}>{t('pr.notAnalyzed')}</div>}

      {/* LA LECTURA ÚNICA (mig 0504): semáforo, cómo se leyó y el índice de formularios y endosos, con los que la
          Declarations lista pero no están en el PDF (paquete incompleto) marcados en rojo. */}
      {data && lectura && (() => {
        const v = lectura.validation; const m = lectura.meta;
        const sem = v.semaforo === 'green' ? 'good' : v.semaforo === 'red' ? 'alert' : 'caution';
        const forms = [...lectura.reading.forms].sort((a, b) => Number(b.listed_in_declarations && !b.found_in_document) - Number(a.listed_in_declarations && !a.found_in_document) || (a.page_from ?? 9999) - (b.page_from ?? 9999));
        const enPdf = forms.filter((f) => f.found_in_document).length;
        return (
          <div className="card" style={{ display: 'grid', gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <h2 className="card-title" style={{ margin: 0 }}>{t('pr.formsTitle')}</h2>
              <span className="badge" style={{ fontSize: 'var(--fs-2xs)', ...sevBadge(sem) }}>{v.semaforo === 'green' ? t('pr.semaforoGreen') : v.semaforo === 'red' ? t('pr.semaforoRed') : t('pr.semaforoYellow')}</span>
              <span style={{ flex: 1 }} />
              <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>
                {m.copied ? t('pr.copiedReading') : t('pr.readingLine', { date: m.read_at ? new Date(m.read_at).toLocaleString() : '—', model: m.rescue_model ?? m.model ?? '—', s: m.latency_ms != null ? `${Math.round(m.latency_ms / 1000)} s` : '—' })}
                {m.rescue_model ? ` · ${t('pr.rescue', { model: m.rescue_model })}` : ''}
              </span>
            </div>
            <div className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{t('pr.formsSub', { found: enPdf, missing: v.forms_missing.length })}{v.findings_modified_by_endorsement ? ` · ${t('pr.modified', { n: v.findings_modified_by_endorsement })}` : ''}</div>
            {v.reasons.length > 0 && v.semaforo !== 'green' && (
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 'var(--fs-xs)' }}>{v.reasons.map((r, i) => <li key={i}>{r}</li>)}</ul>
            )}
            <div style={{ display: 'grid', gap: 2, fontSize: 'var(--fs-xs)', maxHeight: 260, overflowY: 'auto' }}>
              {forms.map((f) => {
                const falta = f.listed_in_declarations && !f.found_in_document;
                return (
                  <div key={f.form_number} style={{ display: 'flex', gap: 8, alignItems: 'baseline', padding: '3px 0', borderBottom: '1px solid var(--line)' }}>
                    <b style={{ whiteSpace: 'nowrap', color: falta ? 'var(--red)' : undefined }}>{f.form_number}</b>
                    <span style={{ flex: 1 }}>{f.title || ''}{f.summary ? <span className="muted"> — {f.summary}</span> : null}{f.modifies.length ? <span className="muted"> · {t('pr.whatChanged', { forms: f.modifies.join(', ') })}</span> : null}</span>
                    <span className="badge gray" style={{ fontSize: 'var(--fs-2xs)' }}>{f.kind.replace('_', ' ')}</span>
                    {falta
                      ? <span className="badge" style={{ fontSize: 'var(--fs-2xs)', ...sevBadge('alert') }}>{t('pr.notInPdf')}</span>
                      : f.page_from
                        ? <button type="button" className="btn ghost sm" style={{ fontSize: 'var(--fs-2xs)', padding: '0 6px' }} onClick={() => goToPage(String(f.page_from))}>{t('pr.pages', { from: f.page_from, to: f.page_to ?? f.page_from })}</button>
                        : null}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {data && (
        <>
          {redFlags.length > 0 && (
            <div className="card" style={{ display: 'grid', gap: 6 }}>
              <h2 className="card-title" style={{ margin: 0 }}><AlertTriangle size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('pr.redFlags')}</h2>
              {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
              {redFlags.map((f: any, i: number) => (
                <div key={i} style={{ fontSize: 'var(--fs-xs)' }}>
                  <span className="badge" style={{ fontSize: 'var(--fs-2xs)', marginRight: 6, ...sevBadge(f.severity) }}>{f.type}</span>{f.detail}
                </div>
              ))}
            </div>
          )}
          {attackRoutes.length > 0 && (
            <div className="card" style={{ display: 'grid', gap: 6 }}>
              <h2 className="card-title" style={{ margin: 0 }}>{t('pr.attackRoutes')}</h2>
              {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
              {attackRoutes.map((r: any, i: number) => (
                <div key={i} style={{ fontSize: 'var(--fs-xs)' }}><b>→ {r.title}</b><div className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{r.rationale}</div></div>
              ))}
            </div>
          )}

          {/* Ray va debajo de Attack Routes y ENCIMA de la póliza. */}
          <RayChat policyId={policyId} onCitePage={goToPage} />

          {/* Secciones extraídas: barra de tabs de lado a lado; abajo 2 columnas —
              izquierda el texto extraído, derecha el PDF embebido (salta de página al click). */}
          {current && (
            <div className="card">
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 10 }}>
                {tabs.map((s) => (
                  <button key={s} className={`btn ghost sm${s === current ? ' active' : ''}`} onClick={() => setActiveTab(s)}>{prettify(s)}</button>
                ))}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 12, alignItems: 'start' }}>
                <div>
                  {Object.entries(data[current] as Record<string, DataPoint>).map(([fieldKey, dp]) => {
                    const selKey = `${current}.${fieldKey}`;
                    return <DataPointRow key={fieldKey} fieldKey={fieldKey} dp={dp} selKey={selKey} selected={selections.has(selKey)} onToggle={toggleSelection} onCitePage={goToPage} />;
                  })}
                </div>
                {/* PDF embebido — sticky para verlo mientras se lee el texto. */}
                <div style={{ position: 'sticky', top: 8 }}>
                  <div className="muted" style={{ fontSize: 'var(--fs-2xs)', marginBottom: 4 }}><FileText size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('pr.pdfPageLabel')} {pdfPage}</div>
                  <iframe
                    key={pdfPage}
                    title="policy-pdf"
                    src={`/api/policy-reader/policies/${policyId}/file.pdf#page=${pdfPage}`}
                    style={{ width: '100%', height: 640, border: '1px solid var(--line)', borderRadius: 8, background: 'var(--surface-2)' }}
                  />
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Panel raíz: lista de pólizas (del claim, o SIN claim si no se pasa claimId) + subir + abrir detalle ──
export function PolicyReader({ claimId, initialPolicyId }: { claimId?: string; initialPolicyId?: string | null }) {
  const { t } = useI18n();
  const [policies, setPolicies] = useState<ListItem[]>([]);
  // `initialPolicyId`: el chip de Files abre directo la póliza ya leída (mig 0504).
  const [selected, setSelected] = useState<string | null>(initialPolicyId ?? null);
  const [claimType, setClaimType] = useState('roof');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const listUrl = claimId ? `/api/policy-reader/claims/${claimId}/policies` : '/api/policy-reader/policies';
  const { fallo, marcar, limpiar } = useFallo();
  const load = useCallback(() => api.get<{ policies: ListItem[] }>(listUrl).then((r) => { limpiar(); setPolicies(r.policies); }).catch(marcar), [listUrl, limpiar, marcar]);
  useEffect(() => { load(); }, [load]);

  async function upload(file: File) {
    setBusy(true); setErr('');
    try {
      const fileKey = await uploadFile(file, 'misc');
      const r = await api.post<{ policy: ListItem }>(listUrl, { fileKey, claimType });
      await load();
      setSelected(r.policy.id);
    } catch (e) { setErr(e instanceof ApiError ? e.message : 'Error'); } finally { setBusy(false); }
  }

  if (selected) return <PolicyReader.Detail policyId={selected} onBack={() => { setSelected(null); load(); }} />;

  return (
    <div className="card" style={{ display: 'grid', gap: 10 }}>
      <h2 className="card-title" style={{ margin: 0 }}><ShieldCheck size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('pr.title')}</h2>
      <div className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{t('pr.subtitle')}</div>
      {fallo && <NoCargo onRetry={load} compact />}
      <div style={{ display: 'grid', gap: 8 }}>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{t('pr.claimType')}:</span>
          <select className="input sm" style={{ width: 130 }} value={claimType} onChange={(e) => setClaimType(e.target.value)}>
            {CLAIM_TYPES.map((c) => <option key={c} value={c}>{c.toUpperCase()}</option>)}
          </select>
        </div>
        <FileDrop onFile={upload} accept="application/pdf" busy={busy}
          label={`${t('pr.uploadPolicy')} — ${t('pr.dropHint')}`} busyLabel={t('pr.uploading')} />
      </div>
      {err && <div style={{ color: 'var(--red)', fontSize: 'var(--fs-xs)' }}>{err}</div>}
      {policies.length === 0 ? (
        <div className="muted" style={{ fontSize: 'var(--fs-sm)' }}>{t('pr.noPolicies')}</div>
      ) : (
        <div style={{ display: 'grid', gap: 6 }}>
          {policies.map((p) => (
            <button key={p.id} className="btn ghost sm" style={{ justifyContent: 'flex-start', textAlign: 'left' }} onClick={() => setSelected(p.id)}>
              <span className="badge gray" style={{ fontSize: 'var(--fs-2xs)', marginRight: 6 }}>{p.claim_type?.toUpperCase()}</span>
              {p.carrier || t('pr.untitledPolicy')}{p.policy_number ? ` · ${p.policy_number}` : ''}
              <span style={{ flex: 1 }} />
              <span className="badge" style={{ fontSize: 'var(--fs-2xs)', ...sevBadge(p.status === 'extracted' ? 'good' : p.status === 'error' ? 'high' : 'medium') }}>{p.status}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
PolicyReader.Detail = PolicyDetail;
